import { graphql, HeadProps, PageProps } from "gatsby";
import React, { FC, useEffect, useMemo } from "react";

import { Breadcrumbs } from "../../components/breadcrumbs";
import {
  BreadcrumbsColor,
  BreadcrumbsPosition,
  BreadcrumbsVariant,
} from "../../components/breadcrumbs/types";
import { SideCtas } from "../../components/buttons/side-ctas";
import { EnrichmentRenderer } from "../../components/enrichment-strip/enrichment-renderer";
import { Footer } from "../../components/footer";
import { HeaderMenuSection } from "../../components/header-menu";
import { GlobalLayout } from "../../components/layouts/global-layout";
import { Renderer } from "../../components/renderer";
import { SEO } from "../../components/seo";
import { mapLabelsByKey } from "../../contexts/labels";
import { useTrackPageview } from "../../hooks/use-track-pageview";
import { getNodeAncestors, getNodeFullPath, getNodesMap } from "../../utils/pages";

export interface StandardPageContext {
  id: string;
  localizedUrls: Record<string, string>;
  locale: string;
  allPageNodes: Queries.StandardPageDataFragment[];
}

export interface StandardPageProps
  extends PageProps<Queries.StandardPageQuery, StandardPageContext> {}

const StandardPage: FC<StandardPageProps> = ({
  data: { contentfulStandardPage, allContentfulLabel, allContentfulRichTextLabel },
  location,
  pageContext: { locale, allPageNodes, localizedUrls },
}) => {
  const breadcrumbs = useMemo(() => {
    const pageNodesMap = getNodesMap(allPageNodes);
    const ancestors = contentfulStandardPage
      ? getNodeAncestors(contentfulStandardPage, pageNodesMap)
      : [];
    return ancestors
      .map((node) => ({
        label: node.breadcrumbTitle ?? node.title ?? "",
        defaultLocaleLabel:
          node.defaultLocaleEntry?.breadcrumbTitle ?? node.defaultLocaleEntry?.title ?? "",
        url: getNodeFullPath(node, pageNodesMap),
      }))
      .filter(({ label }) => label);
  }, [allPageNodes, contentfulStandardPage]);

  const contentGroup = contentfulStandardPage?.defaultLocaleEntry?.contentGroup ?? null;
  const trackPageview = useTrackPageview();
  useEffect(() => {
    trackPageview({
      breadcrumbs,
      content_group: contentGroup,
      locale,
    });
  }, [trackPageview, locale, breadcrumbs, contentGroup]);

  if (!contentfulStandardPage) return null;

  const {
    strips,
    sideCtas,
    enrichmentField,
    header,
    contentful_id,
    breadcrumbsColor,
    breadcrumbsPosition,
    breadcrumbsVariant,
  } = contentfulStandardPage;

  const labels = mapLabelsByKey(Array.from(allContentfulLabel.nodes), (value) => value ?? "");
  const richTextLabels = mapLabelsByKey(
    Array.from(allContentfulRichTextLabel.nodes),
    (value) => value?.raw ?? ""
  );

  return (
    <GlobalLayout
      {...{ labels, richTextLabels, localizedUrls, locale }}
      outerChildren={sideCtas && <SideCtas {...sideCtas} />}
      pageNodes={allPageNodes ?? []}
      currentPage={contentfulStandardPage}
    >
      {header && header.__typename === "ContentfulHeaderMenu" && (
        <HeaderMenuSection
          headerMenu={header}
          pageContentfulId={contentful_id}
          internalPagePath={null}
        />
      )}
      <div className="relative flex flex-col flex-grow">
        {breadcrumbs.length > 1 && (
          <Breadcrumbs
            items={breadcrumbs}
            color={(breadcrumbsColor as BreadcrumbsColor) ?? undefined}
            position={(breadcrumbsPosition as BreadcrumbsPosition) ?? undefined}
            variant={(breadcrumbsVariant as BreadcrumbsVariant) ?? undefined}
          />
        )}
        {enrichmentField && <EnrichmentRenderer {...enrichmentField} />}
        {strips?.map((strip, index) => {
          if (!strip) return null;
          return <Renderer key={index} index={index} {...strip} location={location} />;
        })}
        {contentfulStandardPage?.footer && <Footer {...contentfulStandardPage?.footer} />}
      </div>
    </GlobalLayout>
  );
};

export const Head: FC<HeadProps<Queries.StandardPageQuery, StandardPageContext>> = ({
  data: { contentfulStandardPage },
  pageContext: { localizedUrls },
}) => {
  if (!contentfulStandardPage) return null;
  const {
    node_locale,
    seoTitle: _seoTitle,
    seoDescription,
    seoImage,
    no_index,
    no_follow,
    title,
  } = contentfulStandardPage;
  console.log(contentfulStandardPage);
  const seoTitle = _seoTitle ?? (title ? `Biesse - ${title}` : null);
  return (
    <SEO
      htmlLang={node_locale}
      pageTitle={title}
      title={seoTitle}
      description={seoDescription}
      image={seoImage?.url ?? null}
      path={localizedUrls[node_locale]}
      alternateUrls={localizedUrls}
      noIndex={no_index}
      noFollow={no_follow}
    />
  );
};

export const query = graphql`
  query StandardPage($id: String, $locale: String) {
    contentfulStandardPage(id: { eq: $id }) {
      id
      contentful_id
      slug
      node_locale
      title
      seoTitle
      seoDescription
      no_index
      no_follow
      breadcrumbTitle
      breadcrumbsColor
      breadcrumbsPosition
      breadcrumbsVariant
      parentPage {
        id
      }
      defaultLocaleEntry {
        title
        breadcrumbTitle
        contentGroup
      }
      seoImage {
        url
      }
      sideCtas {
        ...SideCtasData
      }
      enrichmentField {
        __typename
        ... on ContentfulCustomerStory {
          ...CustomerStoryStripData
        }
        ... on ContentfulExhibition {
          ...ExhibitionStripData
        }
        ... on ContentfulProduct {
          ...ProductStripData
        }
        ... on ContentfulWhatsNext {
          ...WhatsNextStripData
        }
      }
      header {
        __typename
        ... on ContentfulHeaderMenu {
          ...HeaderMenuData
        }
      }
      strips {
        __typename
        ... on ContentfulHeroSliderSection {
          ...HeroSliderSectionData
        }
        ... on ContentfulMaterialsSlider {
          ...MaterialsSliderData
        }
        ... on ContentfulAnimatedSliderSection {
          ...AnimatedSliderSectionData
        }
        ... on ContentfulVerticalSliderSection {
          ...VerticalSliderSectionData
        }
        ... on ContentfulParallaxBannerSection {
          ...ParallaxBannerSectionData
        }
        ... on ContentfulEditorialText {
          ...EditorialTextData
        }
        ... on ContentfulBannerImage {
          ...BannerImageData
        }
        ... on ContentfulWorldMap {
          ...WorldMapData
        }
        ... on ContentfulHeroBanner {
          ...HeroBannerData
        }
        ... on ContentfulBannerGrid {
          ...BannerGridData
        }
        ... on ContentfulBannerZip {
          ...BannerZipData
        }
        ... on ContentfulBannerCards {
          ...BannerCardsData
        }
        ... on ContentfulAccordionSection {
          ...AccordionSectionData
        }
        ... on ContentfulEmphasisSliderSection {
          ...EmphasisSliderSectionData
        }
        ... on ContentfulGrid {
          ...GridData
        }
        ... on ContentfulSpecialContent {
          ...SpecialContentData
        }
        ... on ContentfulJobsGridSection {
          ...JobsGridSectionData
        }
        ... on ContentfulTableSection {
          ...TableSectionData
        }
        ... on ContentfulTabsSection {
          ...TabsSectionData
        }
        ... on ContentfulHeroPortrait {
          ...HeroPortraitData
        }
        ... on ContentfulCompanyCalendar {
          ...CompanyCalendarData
        }
        ... on ContentfulFileCategory {
          ...FileCategoryData
        }
        ... on ContentfulEmbeddedContent {
          ...EmbeddedContentData
        }
        ... on ContentfulAnchor {
          ...AnchorData
        }
        ... on ContentfulProductFinderSection {
          ...ProductFinderSectionData
        }
        ... on ContentfulEventsGridSection {
          ...EventsGridSectionData
        }
      }
      footer {
        ...FooterData
      }
    }
    allContentfulLabel(filter: { node_locale: { eq: $locale } }) {
      nodes {
        node_locale
        internalName
        value
      }
    }

    allContentfulRichTextLabel(filter: { node_locale: { eq: $locale } }) {
      nodes {
        node_locale
        internalName
        value {
          raw
        }
      }
    }
    site {
      siteMetadata {
        siteUrl
      }
    }
  }
`;

export default StandardPage;
